import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Facebook, Instagram } from "lucide-react";

// TikTok isn't in lucide — inline the official-looking glyph
function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.72a8.15 8.15 0 0 0 4.77 1.52V6.79a4.85 4.85 0 0 1-1.84-.1Z" />
    </svg>
  );
}

/**
 * Renders the visible social buttons.
 * variant: "footer" (light bg, ink) | "dark" (over dark bg, sand) | "compact" (small inline)
 */
export default function SocialLinks({ variant = "footer", label = "Follow us" }) {
  const [links, setLinks] = useState({ facebook: "", instagram: "", tiktok: "" });

  useEffect(() => {
    api.get("/social").then((r) => setLinks(r.data)).catch(() => {});
  }, []);

  const items = [
    { key: "facebook", url: links.facebook, Icon: Facebook, name: "Facebook" },
    { key: "instagram", url: links.instagram, Icon: Instagram, name: "Instagram" },
    { key: "tiktok", url: links.tiktok, Icon: TikTokIcon, name: "TikTok" },
  ].filter((i) => i.url && i.url.trim().length > 0);

  if (items.length === 0) return null;

  const styles = {
    footer: {
      wrap: "flex items-center gap-3",
      lbl: "overline mr-1",
      btn: "w-9 h-9 rounded-full border border-[var(--line)] flex items-center justify-center text-[var(--ink-soft)] hover:text-[var(--forest)] hover:border-[var(--terracotta)] transition-colors",
      icon: "w-4 h-4",
    },
    dark: {
      wrap: "flex items-center gap-3",
      lbl: "overline text-[var(--sand)] mr-1",
      btn: "w-9 h-9 rounded-full border border-white/25 flex items-center justify-center text-white/80 hover:text-[var(--sand)] hover:border-[var(--sand)] transition-colors",
      icon: "w-4 h-4",
    },
    compact: {
      wrap: "flex items-center gap-2",
      lbl: "hidden",
      btn: "w-8 h-8 rounded-full flex items-center justify-center text-[var(--ink-soft)] hover:text-[var(--forest)] transition-colors",
      icon: "w-4 h-4",
    },
  }[variant];

  return (
    <div data-testid="social-links" className={styles.wrap}>
      {label && <span className={styles.lbl}>{label}</span>}
      {items.map(({ key, url, Icon, name }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Follow us on ${name}`}
          title={name}
          data-testid={`social-${key}`}
          className={styles.btn}
        >
          <Icon className={styles.icon} />
        </a>
      ))}
    </div>
  );
}
